<template>
  <div>
    <b-modal
      id="redeem-gift-card-modal"
      ref="redeem-gift-card-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
      size="md"
    >
      <div class="redeem-gift-card-modal">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">Load Gift Card Code</h2>
          <button type="button" class="close-btn" @click="closeModal" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="modal-content">
          <!-- Step 1: Enter Coupon Code -->
          <div v-if="step === 1" class="step-enter-code">
            <!-- Icon Section -->
            <div class="icon-section">
              <svg xmlns="http://www.w3.org/2000/svg" width="56" height="50" viewBox="0 0 56 50" fill="none"><path d="M48.1792 10.455H44.2467C44.7427 9.46864 45.0227 8.38778 45.0227 7.2986C45.0227 3.8475 42.4976 0.354492 37.6742 0.354492C32.7054 0.354492 29.6772 3.99916 27.9781 7.19625C26.2791 3.99916 23.2509 0.354492 18.282 0.354492C13.4586 0.354492 10.9335 3.8475 10.9335 7.2986C10.9335 8.38778 11.2136 9.46864 11.7095 10.455H7.77711C3.9475 10.455 0.833008 13.5695 0.833008 17.3991V42.6504C0.833008 46.48 3.9475 49.5945 7.77711 49.5945H48.1792C52.0088 49.5945 55.1233 46.48 55.1233 42.6504V17.3991C55.1233 13.5695 52.0088 10.455 48.1792 10.455ZM37.6742 4.14218C40.3029 4.14218 41.2351 5.84245 41.2351 7.2986C41.2351 8.75474 40.3029 10.455 37.6742 10.455H30.6224C31.6875 7.89334 33.8885 4.14218 37.6742 4.14218ZM18.282 4.14218C22.0435 4.14218 24.2516 7.89243 25.3255 10.455H18.282C15.6533 10.455 14.7212 8.75474 14.7212 7.2986C14.7212 5.84245 15.6533 4.14218 18.282 4.14218ZM51.3355 42.6504C51.3355 44.3913 49.9201 45.8068 48.1791 45.8068H7.77711C6.03616 45.8068 4.6207 44.3913 4.6207 42.6504V39.494H51.3356L51.3355 42.6504ZM51.3355 35.7063H4.6207V17.3991C4.6207 15.6582 6.03616 14.2427 7.77711 14.2427H24.191L18.8887 21.3126C18.2598 22.1497 18.43 23.3371 19.2659 23.9647C20.1044 24.5923 21.2929 24.4184 21.9168 23.5862L27.9781 15.5052L34.0394 23.5862C34.4093 24.0818 34.9789 24.3432 35.556 24.3432C35.9505 24.3432 36.35 24.2199 36.6903 23.9647C37.5262 23.3371 37.6964 22.1497 37.0676 21.3126L31.7652 14.2427H48.1791C49.9201 14.2427 51.3355 15.6582 51.3355 17.3991V35.7063Z" fill="white"></path></svg>
            </div>

            <p class="instruction-text">
              Enter your Gift Card Code to load the value of your gift card into CanPay
            </p>

            <div class="form-section">
              <label class="form-label">Gift Card Code <span class="required-asterisk">*</span></label>
              <div class="coupon-code-input-container">
                <input
                  ref="couponInput"
                  v-model="couponCode"
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  maxlength="14"
                  @input="formatCouponCode"
                  @keydown="handleKeydown"
                  class="coupon-input"
                  :class="{ 'form-input-error': couponCodeError }"
                />
              </div>
              <div v-if="couponCodeError" class="field-error-message">
                <b>{{ couponCodeError }}</b>
              </div>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="action-button primary-button"
                @click="validateCoupon"
                :disabled="validating || !isValidLength"
              >
                {{ validating ? 'Validating...' : 'Load Gift Card Value' }}
              </button>
              <button type="button" class="action-button secondary-button" @click="closeModal">
                Cancel
              </button>
            </div>
          </div>

          <!-- Step 2: Confirm Adding Points -->
          <div v-if="step === 2" class="step-confirm">
            <!-- Icon Section -->
            <div class="icon-section">
              <svg xmlns="http://www.w3.org/2000/svg" width="56" height="50" viewBox="0 0 56 50" fill="none"><path d="M48.1792 10.455H44.2467C44.7427 9.46864 45.0227 8.38778 45.0227 7.2986C45.0227 3.8475 42.4976 0.354492 37.6742 0.354492C32.7054 0.354492 29.6772 3.99916 27.9781 7.19625C26.2791 3.99916 23.2509 0.354492 18.282 0.354492C13.4586 0.354492 10.9335 3.8475 10.9335 7.2986C10.9335 8.38778 11.2136 9.46864 11.7095 10.455H7.77711C3.9475 10.455 0.833008 13.5695 0.833008 17.3991V42.6504C0.833008 46.48 3.9475 49.5945 7.77711 49.5945H48.1792C52.0088 49.5945 55.1233 46.48 55.1233 42.6504V17.3991C55.1233 13.5695 52.0088 10.455 48.1792 10.455ZM37.6742 4.14218C40.3029 4.14218 41.2351 5.84245 41.2351 7.2986C41.2351 8.75474 40.3029 10.455 37.6742 10.455H30.6224C31.6875 7.89334 33.8885 4.14218 37.6742 4.14218ZM18.282 4.14218C22.0435 4.14218 24.2516 7.89243 25.3255 10.455H18.282C15.6533 10.455 14.7212 8.75474 14.7212 7.2986C14.7212 5.84245 15.6533 4.14218 18.282 4.14218ZM51.3355 42.6504C51.3355 44.3913 49.9201 45.8068 48.1791 45.8068H7.77711C6.03616 45.8068 4.6207 44.3913 4.6207 42.6504V39.494H51.3356L51.3355 42.6504ZM51.3355 35.7063H4.6207V17.3991C4.6207 15.6582 6.03616 14.2427 7.77711 14.2427H24.191L18.8887 21.3126C18.2598 22.1497 18.43 23.3371 19.2659 23.9647C20.1044 24.5923 21.2929 24.4184 21.9168 23.5862L27.9781 15.5052L34.0394 23.5862C34.4093 24.0818 34.9789 24.3432 35.556 24.3432C35.9505 24.3432 36.35 24.2199 36.6903 23.9647C37.5262 23.3371 37.6964 22.1497 37.0676 21.3126L31.7652 14.2427H48.1791C49.9201 14.2427 51.3355 15.6582 51.3355 17.3991V35.7063Z" fill="white"></path></svg>
            </div>

            <h3 class="confirm-title">Confirm Adding Gift Card to CanPay</h3>

            <div class="gift-card-info">
              <div class="info-row">
                <span class="info-label">Amount:</span>
                <span class="info-value">${{ formatAmount(giftCardInfo.amount) }}</span>
              </div>
              <div class="info-row" v-if="giftCardInfo.merchant_name">
                <span class="info-label">Merchant:</span>
                <span class="info-value">{{ giftCardInfo.merchant_name }}</span>
              </div>
            </div>

            <p class="confirmation-text">
              Are you sure you want to add these gift card points? This action cannot be undone.
            </p>

            <!-- Redemption Error Message -->
            <div v-if="redemptionError" class="redemption-error-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{{ redemptionError }}</span>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="action-button primary-button"
                @click="redeemGiftCard"
                :disabled="redeeming"
              >
                {{ redeeming ? 'Adding Points...' : 'Add Points' }}
              </button>
              <button type="button" class="action-button secondary-button" @click="goBack">
                Back
              </button>
            </div>
          </div>

          <!-- Step 3: Success -->
          <div v-if="step === 3" class="step-success">
            <div class="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 45 45" fill="none" class="ml-2"><path d="M45 22.5C45 34.9264 34.9264 45 22.5 45C10.0736 45 0 34.9264 0 22.5C0 10.0736 10.0736 0 22.5 0C34.9264 0 45 10.0736 45 22.5Z" fill="black"></path><path d="M37.3065 28.5767C39.194 24.5672 38.4215 20.7332 38.2029 19.7162C37.9551 18.5674 37.3283 15.8529 35.0472 13.4165C33.9607 12.2716 32.6704 11.3409 31.2428 10.6727C29.3188 9.77272 27.679 9.59712 26.7826 9.50932C23.0876 9.14348 20.0339 10.2264 18.3285 11.0019C19.1588 10.2033 20.1079 9.53928 21.1417 9.03373C22.0552 8.59538 23.0236 8.28281 24.0204 8.1045C25.6526 7.79137 27.326 7.7592 28.969 8.00939C29.7269 8.12646 32.8899 8.67521 36.0456 11.1336C40.6444 14.7115 42.532 19.6796 41.4606 25.394C39.8281 34.1375 31.4761 39.2592 23.0074 37.0056C20.5149 36.3398 17.8912 34.7667 16.7251 33.2302C16.7834 33.2302 16.849 33.2155 16.8855 33.2375C17.177 33.4277 17.4612 33.6326 17.76 33.8228C22.4972 36.8081 27.351 36.83 32.1757 34.1594C32.7866 33.8204 33.367 33.4287 33.9103 32.9887C36.0019 31.2912 36.9566 29.3157 37.3065 28.5767Z" fill="white"></path><path d="M41.8619 29.1034C41.8109 29.4107 41.7234 29.8497 41.5849 30.3692C40.5792 34.0861 38.2835 36.4348 37.2996 37.4152C33.6993 41.0078 29.5087 42.0467 27.592 42.5004C23.6492 43.4296 20.4643 43.0345 19.3201 42.8516C15.2024 42.1931 12.3746 40.5249 11.5074 39.9761C9.738 38.8608 8.15213 37.476 6.80661 35.8714C5.23513 34.005 4.00402 31.8749 3.16989 29.579C2.95854 28.9937 2.15686 26.6962 1.9528 23.55C1.76331 20.5502 2.21517 18.3112 2.32449 17.7917C2.68091 16.0946 3.25668 14.4515 4.03717 12.9042C4.44529 12.114 6.52237 8.25072 11.1138 5.36793C12.1779 4.69479 14.9619 3.11438 18.9047 2.5144C19.9469 2.35344 28.933 1.10959 35.3683 6.76543C38.3491 9.38482 40.1055 12.8895 40.3824 13.4529C40.8088 14.3232 41.1788 15.2201 41.4902 16.1382C41.3882 15.9992 40.6302 14.9309 40.6083 14.9016C37.8098 10.7384 34.5593 8.77753 34.5593 8.77753C33.3302 8.05264 32.0087 7.49882 30.6311 7.13126C26.251 5.94595 22.5633 6.99956 21.6887 7.26297C16.558 8.8068 13.5189 12.9115 13.3439 13.1529C10.2465 17.4405 10.3121 21.9184 10.3704 23.1622C10.5672 27.3474 12.3892 30.3034 13.22 31.496C13.2856 31.5765 13.3804 31.7009 13.497 31.8472C13.5844 31.9643 13.6719 32.0813 13.7594 32.1984C15.9312 35.0666 18.8099 37.064 22.0094 38.0591C24.3937 38.7911 26.9131 38.9659 29.375 38.5703C31.837 38.1746 34.1762 37.2189 36.2137 35.7763C38.0065 34.5032 39.0924 33.1788 39.7629 32.3594C40.6521 31.2765 41.3007 30.2083 41.5121 29.7839C41.5412 29.7327 41.8473 29.1034 41.8619 29.1034Z" fill="#FFCB11"></path><path d="M18.4306 29.1693C14.5825 29.1693 12.6585 26.6158 12.6585 22.7013C12.6585 18.6551 14.6845 16.2187 18.6492 16.2187C20.1797 16.2187 21.4114 16.5845 22.3588 17.3601C23.2479 18.1576 23.7435 19.2405 23.8674 20.616H22.3224C21.7685 20.616 21.3676 20.3599 21.1271 19.8551C20.7044 18.9551 19.8736 18.4942 18.6565 18.4942C16.2806 18.4942 15.2238 20.177 15.2238 22.7086C15.2238 25.167 16.2369 26.9011 18.5763 26.9011C20.1797 26.9011 21.098 26.0158 21.3749 24.6256H23.8601C23.6342 27.523 21.6009 29.1693 18.4306 29.1693Z" fill="white"></path><path d="M31.7171 16.248H27.3515C26.6155 16.248 26.0324 16.8919 26.0324 17.6309V29.1255H28.6561V24.5379H31.9065C34.4792 24.5379 35.8566 22.9282 35.8566 20.3893C35.8566 17.7114 34.4428 16.248 31.7171 16.248ZM31.4183 22.2112H28.8602V18.4796H31.5494C32.7738 18.4796 33.4006 19.1016 33.4006 20.3527C33.4079 21.6039 32.752 22.2331 31.4183 22.2112Z" fill="#FFCB11"></path></svg>
            </div>
            <h3 class="success-title">Gift Card Added Successfully</h3>
            <p class="success-message">
              ${{ formatAmount(giftCardInfo.amount) }} Gift Card has been added successfully to your CanPay Points.
            </p>
            <button type="button" class="action-button primary-button" @click="closeAndRefresh">
              Done
            </button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from '@/api/giftcard.js';

export default {
  name: 'RedeemGiftCardModal',
  data() {
    return {
      step: 1, // 1: Enter code, 2: Confirm, 3: Success
      couponCode: '', // Full coupon code
      couponCodeError: '',
      redemptionError: '', // Error message for redemption step
      validating: false,
      redeeming: false,
      giftCardInfo: null
    };
  },
  computed: {
    // Check if code has valid length (12 digits + 2 hyphens = 14 chars)
    isValidLength() {
      const cleaned = this.couponCode.replace(/-/g, '');
      return cleaned.length === 12;
    }
  },
  methods: {
    showModal() {
      this.resetForm();
      if (this.$refs['redeem-gift-card-modal']) {
        this.$refs['redeem-gift-card-modal'].show();
      }
    },

    closeModal() {
      if (this.$refs['redeem-gift-card-modal']) {
        this.$refs['redeem-gift-card-modal'].hide();
      }
    },

    resetForm() {
      this.step = 1;
      this.couponCode = '';
      this.couponCodeError = '';
      this.redemptionError = '';
      this.validating = false;
      this.redeeming = false;
      this.giftCardInfo = null;
    },

    handleKeydown(e) {
      // Allow: backspace, delete, tab, escape, enter, arrows
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'];

      if (allowedKeys.includes(e.key)) {
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Only allow alphanumeric characters
      if (!/^[a-zA-Z0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },

    formatCouponCode() {
      // Remove all non-alphanumeric characters and convert to uppercase
      let value = this.couponCode.toUpperCase().replace(/[^A-Z0-9]/g, '');

      // Limit to 12 characters
      value = value.substring(0, 12);

      // Add hyphens after every 4 characters
      let formatted = '';
      for (let i = 0; i < value.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formatted += '-';
        }
        formatted += value[i];
      }

      this.couponCode = formatted;
      this.couponCodeError = '';
    },

    async validateCoupon() {
      this.validating = true;
      this.couponCodeError = '';

      try {
        const response = await api.validateCouponCode({
          coupon_code: this.couponCode
        });

        if (response.code === 200 && response.data.is_valid) {
          this.giftCardInfo = response.data;
          this.step = 2;
        } else {
          this.couponCodeError = response.message || 'Invalid coupon code';
        }
      } catch (err) {
        this.couponCodeError = 'Failed to validate coupon code';
        console.error('Validation error:', err);
      } finally {
        this.validating = false;
      }
    },

    redeemGiftCard() {
      this.redeeming = true;
      this.redemptionError = '';

      api.redeemGiftCard({
        coupon_code: this.couponCode
      })
        .then((response) => {
          this.redeeming = false;
          if (response.code === 200) {
            this.giftCardInfo = { ...this.giftCardInfo, ...response.data };
            this.step = 3;
            this.$emit('gift-card-redeemed', response.data);
          } else {
            // Display error message in the modal instead of closing it
            this.redemptionError = response.message || 'Failed to redeem gift card';
          }
        })
        .catch((err) => {
          this.redeeming = false;
          // Extract error message from API response
          const msg = (err && err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : 'Failed to redeem gift card. Please try again.';
          this.redemptionError = msg;
          console.log('Redemption error:', err);
        });
    },

    goBack() {
      this.step = 1;
      this.giftCardInfo = null;
      this.redemptionError = '';
    },

    closeAndRefresh() {
      this.closeModal();
      this.$emit('redemption-complete');
    },

    formatAmount(amount) {
      return parseFloat(amount).toFixed(2);
    }
  }
};
</script>

<style lang="scss" scoped>
.redeem-gift-card-modal {
  font-family: 'Open Sans', sans-serif;
  background-color: #179346;
  border-radius: 12px;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    .modal-title {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      color: white;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.1);
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: white;
      padding: 0;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: rotate(90deg);
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }

  .modal-content {
    padding: 24px;
  }

  .icon-section {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
    animation: fadeInDown 0.5s ease-out;

    .main-icon {
      filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
    }
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .instruction-text {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 24px;
    text-align: center;
    line-height: 1.6;
  }

  .form-section {
    margin-bottom: 24px;
  }

  .form-label {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: white;
    margin-bottom: 10px;

    .required-asterisk {
      color: #ffeb3b;
      margin-left: 2px;
    }
  }

  .coupon-code-input-container {
    display: flex;
    align-items: center;
    background-color: white;
    border-radius: 12px;
    padding: 0 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &:focus-within {
      border-color: white;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
      transform: translateY(-2px);
    }

    .coupon-input {
      flex: 1;
      width: 100%;
      border: none;
      outline: none;
      padding: 16px 0;
      font-size: 17px;
      font-family: 'Courier New', monospace;
      font-weight: 600;
      letter-spacing: 2px;
      background: transparent;
      color: #1a1a1a;
      text-align: center;

      &::placeholder {
        color: #aaa;
        letter-spacing: 1.5px;
        font-weight: 500;
        text-align: center;
      }
    }
  }

  .field-error-message {
    color: #ffeb3b;
    font-size: 13px;
    margin-top: 5px;

    b {
      font-weight: 600;
    }
  }

  .confirm-title {
    font-size: 22px;
    font-weight: 600;
    color: white;
    margin-bottom: 24px;
    text-align: center;
  }

  .gift-card-info {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    backdrop-filter: blur(10px);

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }

      &:first-child {
        padding-top: 0;
      }

      .info-label {
        font-size: 15px;
        color: rgba(255, 255, 255, 0.9);
        font-weight: 500;
      }

      .info-value {
        font-size: 15px;
        color: white;
        font-weight: 600;
      }
    }
  }

  .confirmation-text {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.95);
    text-align: center;
    margin-bottom: 24px;
    line-height: 1.6;
  }

  .redemption-error-message {
    background: rgba(255, 235, 59, 0.2);
    border: 2px solid #ffeb3b;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    animation: fadeInShake 0.5s ease-out;

    svg {
      flex-shrink: 0;
      color: #ffeb3b;
      margin-top: 2px;
    }

    span {
      flex: 1;
      color: #ffeb3b;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.5;
      text-align: left;
    }
  }

  @keyframes fadeInShake {
    0% {
      opacity: 0;
      transform: translateX(-10px);
    }
    50% {
      transform: translateX(5px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }

  .action-button {
    width: 100%;
    padding: 16px 24px;
    font-size: 16px;
    font-weight: 700;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Open Sans', sans-serif;
    text-transform: none;
    letter-spacing: 0.5px;

    &.primary-button {
      background-color: #ffffff;
      color: #179346;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

      &:hover:not(:disabled) {
        background-color: #f5f5f5;
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
      }

      &:active:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }

      &:disabled {
        background-color: rgba(255, 255, 255, 0.5);
        color: rgba(23, 147, 70, 0.5);
        cursor: not-allowed;
        opacity: 0.6;
        box-shadow: none;
      }
    }

    &.secondary-button {
      background-color: transparent;
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.8);

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        border-color: white;
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .step-success {
    text-align: center;
    padding: 20px 0;

    .success-icon {
      margin-bottom: 24px;
      display: flex;
      justify-content: center;
      animation: scaleIn 0.5s ease-out;

    }

    .success-title {
      font-size: 26px;
      font-weight: 700;
      color: white;
      margin-bottom: 16px;
    }

    .success-message {
      font-size: 17px;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 32px;
      line-height: 1.6;
    }
  }

  @keyframes scaleIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
}

// Override Bootstrap modal styles
::v-deep .modal-content {
  background-color: #179346;
  border: none;
  border-radius: 12px;
}

::v-deep .modal-body {
  padding: 0;
}

::v-deep .modal-dialog {
  margin: 1.75rem auto;
}

@media (max-width: 768px) {
  .redeem-gift-card-modal {
    .modal-header {
      padding: 20px;

      .modal-title {
        font-size: 20px;
      }

      .close-btn {
        width: 32px;
        height: 32px;

        svg {
          width: 18px;
          height: 18px;
        }
      }
    }

    .modal-content {
      padding: 20px;
    }

    .icon-section {
      margin-bottom: 16px;

      .main-icon {
        width: 56px;
        height: 56px;
      }
    }

    .instruction-text,
    .confirmation-text {
      font-size: 14px;
    }

    .form-label {
      font-size: 14px;
    }

    .coupon-code-input-container {
      .coupon-input {
        font-size: 14px;
        padding: 12px 0;
      }
    }

    .action-button {
      padding: 13px 20px;
      font-size: 15px;
    }

    .confirm-title {
      font-size: 20px;
    }

    .gift-card-info {
      padding: 16px;

      .info-row {
        padding: 10px 0;

        .info-label,
        .info-value {
          font-size: 14px;
        }
      }
    }

    .step-success {
      padding: 16px 0;

      .success-icon {
        margin-bottom: 20px;

        svg {
          width: 70px;
          height: 70px;
        }
      }

      .success-title {
        font-size: 22px;
      }

      .success-message {
        font-size: 15px;
        margin-bottom: 28px;
      }
    }
  }

  ::v-deep .modal {
    padding: 0 !important;
  }

  ::v-deep .modal.show {
    display: block !important;
  }

  ::v-deep .modal-dialog {
    margin: 1rem;
  }
}

@media (max-width: 480px) {
  .redeem-gift-card-modal {
    .modal-header {
      padding: 16px;

      .modal-title {
        font-size: 18px;
      }

      .close-btn {
        width: 28px;
        height: 28px;

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }

    .modal-content {
      padding: 16px;
    }

    .icon-section {
      margin-bottom: 12px;

      .main-icon {
        width: 48px;
        height: 48px;
      }
    }

    .coupon-code-input-container {
      padding: 0 14px;

      .coupon-input {
        font-size: 14px;
        letter-spacing: 1.5px;
        padding: 14px 0;
      }
    }

    .action-button {
      padding: 14px 20px;
      font-size: 15px;
    }
  }

  ::v-deep .modal-dialog {
    margin: 0.5rem;
  }
}

::v-deep .modal {
  z-index: 1050 !important;
}

::v-deep .modal-backdrop {
  z-index: 1040 !important;
}
</style>


